import { useState, useEffect, useRef } from "react";
import { Box, Typography, useTheme } from "@mui/material";
import { tokens } from "../../theme";
import BurnTrackerService from "../../services/BurnTrackerService";
import Odometer from 'react-odometerjs';
import 'odometer/themes/odometer-theme-default.css';

// BurnCounter-Komponente mit Animation
const BurnCounter = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const [burnedAmount, setBurnedAmount] = useState(0);
  const [displayValue, setDisplayValue] = useState("0");
  const [isIncreasing, setIsIncreasing] = useState(false);
  const previousValueRef = useRef(0);
  const animationTimeoutRef = useRef(null);
  const initialLoadRef = useRef(true);
  
  // Function to format large numbers with thousand separators
  const formatNumber = (value) => {
    return new Intl.NumberFormat('en-US').format(Math.round(value));
  };

  // Animation of number changes - nur bei tatsächlicher Erhöhung
  const animateValue = (start, end, duration) => {
    // Abbruch bei kleinen Änderungen oder Verringerungen
    if (start >= end || end - start < 10) {
      setDisplayValue(formatNumber(end));
      return;
    }
    
    if (animationTimeoutRef.current) {
      clearTimeout(animationTimeoutRef.current);
    }
    
    // Animation starten
    setIsIncreasing(true);
    
    // Für initiales Laden ohne Animation
    if (initialLoadRef.current) {
      setDisplayValue(formatNumber(end));
      initialLoadRef.current = false;
      return;
    }
    
    // Animation starten
    let startTimestamp = null;
    const step = (timestamp) => {
      if (!startTimestamp) startTimestamp = timestamp;
      const progress = Math.min((timestamp - startTimestamp) / duration, 1);
      const currentValue = Math.floor(progress * (end - start) + start);
      
      setDisplayValue(formatNumber(currentValue));
      
      if (progress < 1) {
        window.requestAnimationFrame(step);
      } else {
        setDisplayValue(formatNumber(end));
        
        // Animation-Status nach kurzer Zeit zurücksetzen
        animationTimeoutRef.current = setTimeout(() => {
          setIsIncreasing(false);
        }, 1000);
      }
    };
    
    window.requestAnimationFrame(step);
  };

  // BurnTrackerService für Daten verwenden
  useEffect(() => {
    // Auf Änderungen im BurnTrackerService reagieren
    const unsubscribe = BurnTrackerService.subscribe(({ currentBurnedAmount }) => {
      if (currentBurnedAmount > 0) {
        setBurnedAmount(currentBurnedAmount);
      }
    });
    return () => {
      unsubscribe();
    };
  }, []);

  return (
    <Box textAlign="center">
      <Typography 
        variant="h3" 
        component="div" 
        sx={{ 
          fontWeight: "bold",
          color: colors.redAccent[500],
          fontSize: { xs: '1.5rem', sm: '1.8rem', md: '2.2rem' },
          letterSpacing: "0.5px",
          minHeight: { xs: '2.5rem', sm: '3rem', md: '3.5rem' },
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          whiteSpace: 'nowrap'
        }}
      >
        <Odometer value={burnedAmount} format="(,ddd)" duration={1500} />
      </Typography>
    </Box>
  );
};

export default BurnCounter;